import { Label } from "@radix-ui/react-label";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { onlyDigits, SectionTitle } from "../EmployeeForm";
import { State, City } from "country-state-city";

function Address({ formData, handleOnChange, setFormData }) {
  const [sameAsPermanent, setSameAsPermanent] = useState(false);
  const [states, setStates] = useState([]);
  const [permanentCities, setPermanentCities] = useState([]);
  const [currentCities, setCurrentCities] = useState([]);

  useEffect(() => {
    if (sameAsPermanent) {
      setFormData((prev) => ({
        ...prev,
        currentAddress: prev.permanentAddress,
        currentState: prev.permanentState,
        currentCity: prev.permanentCity,
        currentPinCode: prev.permanentPinCode,
      }));

      if (formData.permanentState) {
        const citiesList = City.getCitiesOfState(
          "IN",
          formData.permanentState,
        ).map((c) => ({
          label: c.name,
          value: c.name,
        }));
        setCurrentCities(citiesList);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.permanentAddress,
    formData.permanentState,
    formData.permanentCity,
    formData.permanentPinCode,
    sameAsPermanent,
  ]);

  useEffect(() => {
    setStates(
      State.getStatesOfCountry("IN").map((s) => ({
        label: s.name,
        value: s.isoCode,
      })),
    );
  }, []);

  useEffect(() => {
    const getStateCode = (value) => {
      if (!value) return null;

      const state = State.getStatesOfCountry("IN").find(
        (s) =>
          s.isoCode === value ||
          s.name.toLowerCase() === String(value).toLowerCase(),
      );

      return state?.isoCode || value;
    };

    const permanentStateCode = getStateCode(formData.permanentState);
    const currentStateCode = getStateCode(formData.currentState);

    if (permanentStateCode) {
      setPermanentCities(
        City.getCitiesOfState("IN", permanentStateCode).map((c) => ({
          label: c.name,
          value: c.name,
        })),
      );
    }

    if (currentStateCode) {
      setCurrentCities(
        City.getCitiesOfState("IN", currentStateCode).map((c) => ({
          label: c.name,
          value: c.name,
        })),
      );
    }

    setFormData((prev) => ({
      ...prev,
      permanentState: permanentStateCode,
      currentState: currentStateCode,
    }));
  }, [formData.permanentState, formData.currentState, setFormData]);

  useEffect(() => {
    const isSame =
      formData.permanentAddress &&
      formData.currentAddress &&
      formData.permanentAddress === formData.currentAddress &&
      formData.permanentState === formData.currentState &&
      formData.permanentCity === formData.currentCity &&
      formData.permanentPinCode === formData.currentPinCode;

    setSameAsPermanent(Boolean(isSame));
  }, [
    formData.permanentAddress,
    formData.currentAddress,
    formData.permanentState,
    formData.currentState,
    formData.permanentCity,
    formData.currentCity,
    formData.permanentPinCode,
    formData.currentPinCode,
  ]);

  const handleStateChange = (type, value) => {
    setFormData((prev) => ({
      ...prev,
      [`${type}State`]: value,
      [`${type}City`]: null,
    }));

    const citiesList = City.getCitiesOfState("IN", value).map((c) => ({
      label: c.name,
      value: c.name,
    }));

    if (type === "permanent") setPermanentCities(citiesList);
    else setCurrentCities(citiesList);
  };

  const handlePinChange = async (type, value) => {
    const pin = onlyDigits(value, 6);
    handleOnChange(`${type}PinCode`, pin);

    if (pin.length !== 6) return;

    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      if (data?.[0]?.Status !== "Success") return;

      const postOffice = data[0].PostOffice[0];
      const stateName = postOffice.State;
      const cityName = postOffice.District;

      const stateObj = State.getStatesOfCountry("IN").find(
        (s) => s.name.toLowerCase() === stateName.toLowerCase(),
      );
      if (!stateObj) return;

      const stateCode = stateObj.isoCode;
      const citiesList = City.getCitiesOfState("IN", stateCode).map((c) => ({
        label: c.name,
        value: c.name,
      }));
      const matchedCity = citiesList.find(
        (c) => c.value.toLowerCase() === cityName.toLowerCase(),
      );

      if (type === "permanent") setPermanentCities(citiesList);
      else setCurrentCities(citiesList);

      setFormData((prev) => ({
        ...prev,
        [`${type}State`]: stateCode,
        [`${type}City`]: matchedCity ? matchedCity.value : cityName,
      }));
    } catch (error) {
      console.error("PIN fetch error:", error);
    }
  };

  const handleSameAddress = (checked) => {
    setSameAsPermanent(checked);
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        currentAddress: prev.permanentAddress,
        currentState: prev.permanentState,
        currentCity: prev.permanentCity,
        currentPinCode: prev.permanentPinCode,
      }));
      setCurrentCities(permanentCities);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Permanent Address</SectionTitle>
      <Label>
        Address <span className="text-red-600">*</span>
      </Label>
      <InputText
        value={formData.permanentAddress}
        onChange={(e) => handleOnChange("permanentAddress", e.target.value)}
        placeholder="Enter Permanent Address"
        className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            State <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.permanentState}
            options={states}
            onChange={(e) => handleStateChange("permanent", e.value)}
            placeholder="Select Permanent State"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            City <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.permanentCity}
            options={permanentCities}
            onChange={(e) => handleOnChange("permanentCity", e.value)}
            placeholder="Select Permanent City"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Pin Code <span className="text-red-600">*</span>
          </Label>
          <InputText
            value={formData.permanentPinCode}
            onChange={(e) => handlePinChange("permanent", e.target.value)}
            placeholder="Permanent Pin Code"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
      </div>
      <div className="flex items-center gap-2 my-2">
        <Label>Current Address</Label>
        <input
          type="checkbox"
          id="sameAddress"
          checked={sameAsPermanent}
          onChange={(e) => handleSameAddress(e.target.checked)}
        />
        <Label htmlFor="sameAddress">Same as Permanent Address</Label>
      </div>
      <Label>
        Address <span className="text-red-600">*</span>
      </Label>
      <InputText
        value={formData.currentAddress}
        onChange={(e) => handleOnChange("currentAddress", e.target.value)}
        disabled={sameAsPermanent}
        placeholder="Enter Current Address"
        className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            State <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.currentState}
            options={states}
            onChange={(e) => handleStateChange("current", e.value)}
            disabled={sameAsPermanent}
            placeholder="Select Current State"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            City <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.currentCity}
            options={currentCities}
            onChange={(e) => handleOnChange("currentCity", e.value)}
            disabled={sameAsPermanent}
            placeholder="Select Current City"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Pin Code <span className="text-red-600">*</span>
          </Label>
          <InputText
            value={formData.currentPinCode}
            onChange={(e) => handlePinChange("current", e.target.value)}
            disabled={sameAsPermanent}
            placeholder="Current Pin Code"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
      </div>
    </section>
  );
}

export default Address;
